import { ServiceException } from '@inter-ikea-kompis/exceptions';
import type { CacheStore } from '@inter-ikea-kompis/utilities';
import { GlobalCacheStore } from '@inter-ikea-kompis/utilities';

const CONNECTION_TIMEOUT = 15000;

type CacheItem = {
	connectedCount: number;
	iframe: HTMLIFrameElement;
	connectionTimeout: ReturnType<typeof setTimeout> | null;
	resolves: Array<(iframe: HTMLIFrameElement) => void>;
	rejects: Array<(error: Error) => void>;
	error: Error | null;
};

/**
 * Service fetch utility.
 */
export default class IframeManager {
	public readonly url: string;
	private cache: Map<string, CacheItem>;
	private isConnected = false;

	/**
	 * Constructor.
	 *
	 * @param url Iframe URL.
	 * @param cacheStore Cache store.
	 */
	constructor(url: string, cacheStore: CacheStore = new GlobalCacheStore()) {
		this.url = url;
		this.cache = cacheStore.getStore('iframeManager');
	}

	/**
	 * Connects the iframe.
	 *
	 * @returns Iframe.
	 */
	public getIframe(): Promise<HTMLIFrameElement> {
		return new Promise((resolve, reject) => {
			const cacheItem = this.cache.get(this.url);
			if (cacheItem) {
				if (!this.isConnected) {
					cacheItem.connectedCount++;
				}
				if (cacheItem.error) {
					reject(cacheItem.error);
				} else if (cacheItem.connectionTimeout === null) {
					resolve(cacheItem.iframe);
				} else {
					cacheItem.resolves.push(resolve);
					cacheItem.rejects.push(reject);
				}
			} else {
				const iframe = document.createElement('iframe');
				const cacheItemDefault: CacheItem = {
					connectedCount: 1,
					iframe,
					connectionTimeout: null,
					resolves: [resolve],
					rejects: [reject],
					error: null
				};

				cacheItemDefault.connectionTimeout = this.getConnectionTimeout(cacheItemDefault);

				this.cache.set(this.url, cacheItemDefault);

				iframe.addEventListener('load', this.onIframeLoad.bind(this, cacheItemDefault));
				iframe.src = this.url;
				iframe.style.display = 'none';

				document.body.appendChild(iframe);
			}

			this.isConnected = true;
		});
	}

	/**
	 * Disconnects the iframe.
	 */
	public disconnect(): void {
		const cacheItem = this.cache.get(this.url);
		if (cacheItem) {
			cacheItem.connectedCount--;
			if (cacheItem.connectedCount === 0) {
				if (cacheItem.connectionTimeout) clearTimeout(cacheItem.connectionTimeout);
				document.body.removeChild(cacheItem.iframe);
				this.cache.delete(this.url);
			}
		}
		this.isConnected = false;
	}

	/**
	 * Triggered on iframe load.
	 *
	 * @param cacheItem Cache item.
	 */
	private onIframeLoad(cacheItem: CacheItem): void {
		if (cacheItem.connectionTimeout) clearTimeout(cacheItem.connectionTimeout);
		cacheItem.connectionTimeout = null;

		if (cacheItem.iframe.contentWindow) {
			const resolves = cacheItem.resolves;
			cacheItem.resolves = [];
			for (const resolve of resolves) {
				resolve(cacheItem.iframe);
			}
		} else {
			const rejects = cacheItem.rejects;
			cacheItem.error = new ServiceException(
				'Failed to load iframe. Missing access to iframe.contentWindow.'
			);
			cacheItem.rejects = [];
			for (const reject of rejects) {
				reject(cacheItem.error);
			}
		}
	}

	/**
	 * Returns a timeout.
	 *
	 * @param cacheItem Cache item.
	 * @returns Timeout.
	 */
	private getConnectionTimeout(cacheItem: CacheItem): CacheItem['connectionTimeout'] {
		return setTimeout(() => {
			const rejects = cacheItem.rejects;
			cacheItem.error = new ServiceException(
				'Failed to load iframe. Request timed out. Connection problems? Trying to load: ' + this.url
			);
			cacheItem.rejects = [];
			for (const reject of rejects) {
				reject(cacheItem.error);
			}
		}, CONNECTION_TIMEOUT);
	}
}
